import React, { useReducer, useRef } from 'react';
import { t, Trans } from '@lingui/macro';
import { Button, Dialog, useToast } from '@economic/taco';

import View from '~/app/shared/components/View/View';
import { Field, Form } from '~/app/shared/components/Form';
import { Column, Row } from '~/app/shared/components/Grid/Grid';

import { invalidateDimensionCache } from '~/app/shared/dimensions/hooks/useDimensions';
import { createDimensionValue } from '~/app/shared/dimensions/api';
import { DimensionValue } from '~/app/shared/dimensions/types';

import { DialogFormProps } from '~/app/forms/types';
import { dimensionValueValidationSchema } from './validationSchemas';
import { onSubmitError } from '../helpers/utils';

type CreateDimensionValueFormProps = DialogFormProps<DimensionValue> & {
    allowSaveAndNew?: boolean;
    allowCreationOfBarred?: boolean;
    showSaveAndApply?: boolean;
};
const CreateDimensionValueForm: React.FC<CreateDimensionValueFormProps> = ({
    allowSaveAndNew = false,
    allowCreationOfBarred = false,
    initialData,
    showSaveAndApply = false,
    onClose,
    onSubmitted,
}) => {
    const closeAfterSubmit = useRef(false);

    const [formKey, setFormKey] = useReducer(v => v + 1, 0);
    const toast = useToast();

    const onSubmittedHandler = (dimensionValue: DimensionValue) => {
        toast.success(t`Dimension value created`);
        invalidateDimensionCache('value', [dimensionValue.key, dimensionValue.dimensionId]).then(() => {
            onSubmitted?.(dimensionValue);
        });

        if (allowSaveAndNew) {
            if (closeAfterSubmit.current) {
                onClose();
            } else {
                setFormKey();
            }
        } else {
            onClose();
        }
    };

    return (
        <View>
            <View.Title>
                <Trans>Create dimension value</Trans>
            </View.Title>
            <View.Content>
                <Form
                    key={formKey}
                    validationSchema={dimensionValueValidationSchema}
                    initialData={initialData}
                    onSubmit={createDimensionValue}
                    onSubmitted={onSubmittedHandler}
                    onSubmitError={err => onSubmitError(err, toast)}
                    skipNotifications>
                    {({ submitting }) => (
                        <>
                            <Field.Hidden name="dimensionId" />
                            <Row>
                                <Column>
                                    <Field.Number label={t`No.`} name="key" required autoFocus />
                                    <Field.Text label={t`Name`} name="name" required />
                                </Column>
                                {allowCreationOfBarred ? (
                                    <>
                                        <div className="border-l border-l-grey-300 mx-4" />
                                        <Column>
                                            <Field.Dropdown
                                                label={t`Access`}
                                                name="active"
                                                defaultValue
                                                readOnly={!allowCreationOfBarred}
                                                options={[
                                                    { text: t`Open`, value: true },
                                                    { text: t`Barred`, value: false },
                                                ]}
                                            />
                                        </Column>
                                    </>
                                ) : (
                                    <Field.Hidden name="active" value />
                                )}
                            </Row>
                            <Row>
                                <div className="mt-6 gap-2 w-full flex justify-start flex-row-reverse">
                                    {allowSaveAndNew ? (
                                        <>
                                            <Button type="submit" appearance="primary" disabled={submitting}>
                                                <Trans>Save & new</Trans>
                                            </Button>

                                            <div
                                                onClick={() => {
                                                    closeAfterSubmit.current = true;
                                                }}>
                                                <Button type="submit" disabled={submitting}>
                                                    <Trans>Save</Trans>
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <Button type="submit" appearance="primary" disabled={submitting}>
                                            {showSaveAndApply ? <Trans>Save & apply</Trans> : <Trans>Save</Trans>}
                                        </Button>
                                    )}

                                    <Dialog.Close>
                                        <Button type="button" onClick={() => onClose()}>
                                            <Trans>Cancel</Trans>
                                        </Button>
                                    </Dialog.Close>
                                </div>
                            </Row>
                        </>
                    )}
                </Form>
            </View.Content>
        </View>
    );
};

export default CreateDimensionValueForm;
